import * as GAME from 'vibegame';
import { Transform, Parent, WorldTransform } from 'vibegame/transforms';

export const ScaleShaker = GAME.defineComponent({
  parent: GAME.Types.eid,
  initialized: GAME.Types.ui8,
});

export const ScaleShakerDriver = GAME.defineComponent({
  radius: GAME.Types.f32,
  amplitude: GAME.Types.f32,
  global: GAME.Types.ui8,
});

const shakerQuery = GAME.defineQuery([ScaleShaker, Transform]);
const driverQuery = GAME.defineQuery([ScaleShakerDriver, Transform]);

const ScaleShakerInitSystem: GAME.System = {
  group: 'setup',
  update(state) {
    for (const eid of shakerQuery(state.world)) {
      if (ScaleShaker.initialized[eid]) continue;
      ScaleShaker.initialized[eid] = 1;

      const existingParent = state.hasComponent(eid, Parent) ? Parent.entity[eid] : 0;

      const shakerParent = state.createEntity();
      state.addComponent(shakerParent, Transform, {
        posX: 0,
        posY: 0,
        posZ: 0,
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
      });

      if (existingParent) {
        state.addComponent(shakerParent, Parent, { entity: existingParent });
      }

      ScaleShaker.parent[eid] = shakerParent;
      Parent.entity[eid] = shakerParent;
    }
  },
};

const ScaleShakerSystem: GAME.System = {
  group: 'simulation',
  update(state) {
    const drivers = Array.from(driverQuery(state.world));

    for (const eid of shakerQuery(state.world)) {
      const parentEid = ScaleShaker.parent[eid];
      if (!parentEid) continue;

      const localX = Transform.posX[eid];
      const localY = Transform.posY[eid];
      const localZ = Transform.posZ[eid];

      const worldX = WorldTransform.posX[eid];
      const worldY = WorldTransform.posY[eid];
      const worldZ = WorldTransform.posZ[eid];

      let totalInfluence = 0;
      for (const driverEid of drivers) {
        const isGlobal = ScaleShakerDriver.global[driverEid];
        const amplitude = ScaleShakerDriver.amplitude[driverEid];

        if (isGlobal) {
          totalInfluence += amplitude;
        } else {
          const centerX = WorldTransform.posX[driverEid];
          const centerY = WorldTransform.posY[driverEid];
          const centerZ = WorldTransform.posZ[driverEid];
          const radius = ScaleShakerDriver.radius[driverEid];

          const dx = worldX - centerX;
          const dy = worldY - centerY;
          const dz = worldZ - centerZ;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          const influence = radius > 0 ? Math.max(0, 1 - dist / radius) : 0;
          totalInfluence += amplitude * influence;
        }
      }

      const scale = 1 + totalInfluence;

      Transform.scaleX[parentEid] = scale;
      Transform.scaleY[parentEid] = scale;
      Transform.scaleZ[parentEid] = scale;

      Transform.posX[parentEid] = localX * (1 - scale);
      Transform.posY[parentEid] = localY * (1 - scale);
      Transform.posZ[parentEid] = localZ * (1 - scale);
    }
  },
};

export const ScaleShakerPlugin: GAME.Plugin = {
  components: { ScaleShaker, ScaleShakerDriver },
  systems: [ScaleShakerInitSystem, ScaleShakerSystem],
  config: {
    defaults: {
      'scale-shaker': {
        parent: 0,
        initialized: 0,
      },
      'scale-shaker-driver': {
        radius: 2,
        amplitude: 0,
        global: 0,
      },
    },
  },
};
